import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { timeCustomersAPI } from '../services/api';
import { TimeCustomerLookup } from '../types';

const apiErrorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const sortCustomers = (customers: TimeCustomerLookup[]) =>
  [...customers].sort((a, b) => a.title.localeCompare(b.title));

const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<TimeCustomerLookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [search, setSearch] = useState('');

  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await timeCustomersAPI.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('[FIX:customers] Failed to load Flow customers', error);
      setLoadError('Customers could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Name-only creation reuses an existing customer when the name already exists.
  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (creating) {
      return;
    }

    const title = newName.trim();
    if (!title) {
      setCreateError('Customer name is required');
      return;
    }

    setCreating(true);
    setCreateError('');
    try {
      const created = await timeCustomersAPI.createCustomer(title);
      setCustomers(current => sortCustomers([...current.filter(customer => customer.id !== created.id), created]));
      setNewName('');
      toast.success('Customer added');
    } catch (error) {
      setCreateError(apiErrorMessage(error, 'Customer could not be created.'));
    } finally {
      setCreating(false);
    }
  };

  const startRenaming = (customer: TimeCustomerLookup) => {
    setEditingId(customer.id);
    setEditingName(customer.title);
    setRenameError('');
  };

  const cancelRenaming = () => {
    setEditingId(null);
    setEditingName('');
    setRenameError('');
  };

  const saveRenamed = async () => {
    if (editingId === null) {
      return;
    }

    const title = editingName.trim();
    if (!title) {
      setRenameError('Customer name is required');
      return;
    }

    setSaving(true);
    setRenameError('');
    try {
      const updated = await timeCustomersAPI.updateCustomer(editingId, title);
      setCustomers(current => sortCustomers(
        current.map(customer => (customer.id === updated.id ? updated : customer))
      ));
      cancelRenaming();
      toast.success('Customer updated');
    } catch (error) {
      setRenameError(apiErrorMessage(error, 'Customer could not be updated.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = async (customer: TimeCustomerLookup) => {
    if (!window.confirm(`Delete customer "${customer.title}"? This cannot be undone.`)) {
      return;
    }

    setDeletingId(customer.id);
    try {
      await timeCustomersAPI.deleteCustomer(customer.id);
      setCustomers(current => current.filter(item => item.id !== customer.id));
      toast.success('Customer deleted');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Customer could not be deleted.'));
    } finally {
      setDeletingId(null);
    }
  };

  const filteredCustomers = search.trim()
    ? customers.filter(customer =>
        customer.title.toLowerCase().includes(search.trim().toLowerCase()))
    : customers;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Customers</h1>
          <p>Shared Flow customers used across leads and projects.</p>
        </div>
      </div>

      <section className="card card-section" aria-label="Add customer">
        <div className="card-body">
          <h2 className="card-title">Add customer</h2>
          <form className="time-form" onSubmit={handleCreate}>
            <div className="form-group">
              <label className="form-label" htmlFor="new-customer-name">
                Customer name <span className="required-mark">*</span>
              </label>
              <input
                id="new-customer-name"
                className="form-control"
                value={newName}
                maxLength={256}
                aria-invalid={createError ? true : undefined}
                onChange={event => {
                  setNewName(event.target.value);
                  if (createError) setCreateError('');
                }}
                disabled={creating}
              />
              {createError && <div className="field-error">{createError}</div>}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Adding...' : 'Add customer'}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="card card-section">
        <div className="card-body">
          <div className="filter-bar">
            <input
              type="search"
              className="form-control"
              aria-label="Search customers"
              placeholder="Search customers..."
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>

          {loading ? (
            <p role="status">Loading customers...</p>
          ) : loadError ? (
            <div role="alert">
              <p>{loadError}</p>
              <button type="button" className="btn btn-secondary" onClick={loadCustomers}>
                Retry
              </button>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <p role="status">No customers found.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover" aria-label="Customers">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map(customer => (
                    <tr key={customer.id}>
                      <td data-label="Name">
                        {editingId === customer.id ? (
                          <>
                            <input
                              className="form-control"
                              aria-label={`Rename ${customer.title}`}
                              value={editingName}
                              maxLength={256}
                              aria-invalid={renameError ? true : undefined}
                              onChange={event => {
                                setEditingName(event.target.value);
                                if (renameError) setRenameError('');
                              }}
                              disabled={saving}
                            />
                            {renameError && <div className="field-error">{renameError}</div>}
                          </>
                        ) : (
                          customer.title
                        )}
                      </td>
                      <td data-label="Actions">
                        <div className="action-row">
                          {editingId === customer.id ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={saveRenamed}
                                disabled={saving}
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={cancelRenaming}
                                disabled={saving}
                                aria-label={`Cancel renaming ${customer.title}`}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                aria-label={`Rename ${customer.title}`}
                                onClick={() => startRenaming(customer)}
                              >
                                Rename
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger"
                                aria-label={`Delete ${customer.title}`}
                                onClick={() => deleteCustomer(customer)}
                                disabled={deletingId === customer.id}
                              >
                                {deletingId === customer.id ? 'Deleting...' : 'Delete'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CustomersPage;
